import { DetailForm } from '@/@diffrag/form/DetailForm'
import { FormFieldType } from '@/@diffrag/form/fields/type'
import type { FormFieldData } from '@/@diffrag/form/fields/type'
import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import type {
  IAdminConsultationRequestCreate,
  IAdminConsultationRequestRO,
  IAdminConsultationRequestUpdate,
} from 'api'
import {
  IAdminConsultationRequestCreateChannelEnum,
  IAdminConsultationRequestCreateStatusEnum,
  IAdminConsultationRequestRoChannelEnum,
  IAdminConsultationRequestRoStatusEnum,
  IAdminConsultationRequestUpdateStatusEnum,
} from 'api'
import { useNavigate } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useUser } from '@/@diffrag/providers/user.provider'
import { AdminRole, type AdminJwtUser } from '@/constants/adminRole'
import dayjs from 'dayjs'
import { toast } from '@/lib/toast'
import { ConsultationFormPayloadPanel } from './ConsultationFormPayloadPanel'
import { CURRENCY_SELECT_OPTIONS } from '@/constants/currencies'
import {
  formatAmountDisplay,
  normalizeAmountForSave,
  parseAmountInput,
} from '@/lib/formatAmount'
import { Input } from '@/components/ui/input'

const PARTNER_NONE = '__none__'

const CHANNEL_OPTIONS = [
  { label: 'WhatsApp', value: IAdminConsultationRequestCreateChannelEnum.WHATSAPP },
  { label: 'LINE', value: IAdminConsultationRequestCreateChannelEnum.LINE },
  { label: '카카오', value: IAdminConsultationRequestCreateChannelEnum.KAKAO },
  { label: '기타', value: IAdminConsultationRequestCreateChannelEnum.OTHER },
]

const CHANNEL_LABELS: Record<string, string> = {
  WEB_FORM: '웹 폼',
  WHATSAPP: 'WhatsApp',
  LINE: 'LINE',
  KAKAO: '카카오',
  OTHER: '기타',
}

const STATUS_OPTIONS = [
  { label: '신규', value: IAdminConsultationRequestRoStatusEnum.NEW },
  { label: '진행중', value: IAdminConsultationRequestRoStatusEnum.IN_PROGRESS },
  {
    label: '예약확정',
    value: IAdminConsultationRequestRoStatusEnum.RESERVATION_CONFIRMED,
  },
  { label: '완료', value: IAdminConsultationRequestRoStatusEnum.COMPLETED },
  { label: '취소', value: IAdminConsultationRequestRoStatusEnum.CANCELLED },
]

type ConsultationFormData = Omit<
  IAdminConsultationRequestRO,
  'referralPartnerId'
> & {
  referralPartnerId: number | null | typeof PARTNER_NONE
  formPayload?: IAdminConsultationRequestRO['formPayload']
}

function formatDateTime(value: string | Date | null | undefined) {
  if (!value) {
    return '-'
  }
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '-'
}

function resolveReferralPartnerId(value: unknown): number | null | undefined {
  if (value === PARTNER_NONE || value === null || value === '') {
    return null
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function normalizeRevenueDate(value: string | Date | undefined): string {
  if (!value) {
    return ''
  }
  return dayjs(value).format('YYYY-MM-DD')
}

function RevenueAmountField() {
  const form = useFormContext<ConsultationFormData>()
  const amount = form.watch('revenueAmount')

  return (
    <Input
      className="max-w-[500px]"
      inputMode="decimal"
      placeholder="0"
      value={formatAmountDisplay(String(amount ?? ''))}
      onChange={(event) => {
        const parsed = parseAmountInput(event.target.value)
        form.setValue('revenueAmount', parsed, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }}
    />
  )
}

function createDefaultFormData(): ConsultationFormData {
  return {
    id: 0,
    referrerCodeSnapshot: '',
    referralPartnerId: PARTNER_NONE,
    referralPartnerName: null,
    linkedRevenueId: null,
    procedureName: '',
    revenueAmount: '',
    revenueCurrency: 'KRW',
    revenueDate: dayjs().format('YYYY-MM-DD'),
    name: '',
    phone: '',
    email: '',
    message: '',
    nationality: null,
    preferredClinic: null,
    preferredVisitDate: null,
    preferredContactChannel: null,
    channel: IAdminConsultationRequestRoChannelEnum.WHATSAPP,
    status: IAdminConsultationRequestRoStatusEnum.NEW,
    reservationConfirmedAt: null,
    createDate: new Date().toISOString(),
    adminMemo: null,
  }
}

export default function ConsultationRequestEdit() {
  const navigate = useNavigate()
  const id = useQueryValue('id', '')
  const isCreate = !id
  const user = useUser() as AdminJwtUser | undefined
  const isPartner = user?.role === AdminRole.PARTNER_ADMIN

  const { data: partners } = useApi({
    id: 'consultationPartnerSelect',
    fn: api().adminReferralPartner.adminReferralPartnerList,
    params: { page: 1 },
  })

  const { data, refetch } = useApi({
    id: 'consultationRequestDetail',
    fn: api().adminConsultationRequest.adminConsultationRequestDetail,
    params: Number(id),
    enabled: !!id,
  })

  const partnerOptions = [
    { label: '(없음)', value: PARTNER_NONE },
    ...(partners?.items.map((p) => ({
      label: `${p.code} (${p.slug})`,
      value: p.id,
    })) ?? []),
  ]

  const formData: ConsultationFormData = isCreate
    ? createDefaultFormData()
    : {
        ...(data as IAdminConsultationRequestRO),
        referralPartnerId: data?.referralPartnerId ?? PARTNER_NONE,
        procedureName: data?.procedureName ?? '',
        revenueAmount: data?.revenueAmount ?? '',
        revenueCurrency: data?.revenueCurrency ?? 'KRW',
        revenueDate: data?.revenueDate
          ? dayjs(data.revenueDate).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
      }

  const onSave = async (formValues: ConsultationFormData) => {
    if (isPartner) {
      return
    }

    const referralPartnerId = resolveReferralPartnerId(formValues.referralPartnerId)
    const partnerCode =
      referralPartnerId != null
        ? partners?.items.find((p) => p.id === referralPartnerId)?.code
        : undefined

    if (isCreate) {
      if (!formValues.name?.trim()) {
        toast.error('이름을 입력해주세요.')
        return
      }
      if (!formValues.phone?.trim()) {
        toast.error('연락처를 입력해주세요.')
        return
      }
      if (!formValues.email?.trim()) {
        toast.error('이메일을 입력해주세요.')
        return
      }
      if (!formValues.message?.trim()) {
        toast.error('상담 내용을 입력해주세요.')
        return
      }

      const createData: IAdminConsultationRequestCreate = {
        name: formValues.name.trim(),
        phone: formValues.phone.trim(),
        email: formValues.email.trim(),
        message: formValues.message.trim(),
        channel:
          formValues.channel as unknown as IAdminConsultationRequestCreateChannelEnum,
        status:
          formValues.status as unknown as IAdminConsultationRequestCreateStatusEnum,
        adminMemo: formValues.adminMemo?.trim() || undefined,
        referralPartnerId: referralPartnerId ?? undefined,
        referrerCode:
          partnerCode ??
          (formValues.referrerCodeSnapshot?.trim() || undefined),
      }

      await api().adminConsultationRequest.adminConsultationRequestCreate(
        createData,
      )
    } else {
      const isCompleted =
        formValues.status === IAdminConsultationRequestRoStatusEnum.COMPLETED

      if (isCompleted && referralPartnerId == null) {
        toast.error('완료 처리하려면 파트너를 지정해주세요.')
        return
      }

      if (isCompleted) {
        if (!formValues.procedureName?.trim()) {
          toast.error('시술명을 입력해주세요.')
          return
        }
        const amount = normalizeAmountForSave(formValues.revenueAmount)
        if (!amount) {
          toast.error('매출액을 입력해주세요.')
          return
        }
        const revenueDate = normalizeRevenueDate(formValues.revenueDate)
        if (!revenueDate) {
          toast.error('매출일을 선택해주세요.')
          return
        }
      }

      const updateData: IAdminConsultationRequestUpdate = {
        status:
          formValues.status as unknown as IAdminConsultationRequestUpdateStatusEnum,
        adminMemo: formValues.adminMemo ?? undefined,
        referralPartnerId,
        ...(isCompleted && {
          procedureName: formValues.procedureName?.trim(),
          revenueAmount: normalizeAmountForSave(formValues.revenueAmount),
          revenueCurrency: formValues.revenueCurrency?.trim() || 'KRW',
          revenueDate: normalizeRevenueDate(formValues.revenueDate),
        }),
      }

      await api().adminConsultationRequest.adminConsultationRequestUpdate(
        Number(id),
        updateData,
      )
    }

    navigate('/consultation-requests', { replace: true })
    refetch()
  }

  const onDelete = async () => {
    if (!id || isPartner) {
      return
    }
    await api().adminConsultationRequest.adminConsultationRequestDelete(Number(id))
  }

  return (
    <ContentDetailView
      title={isCreate ? '상담 신청 등록' : '상담 신청 내역 상세'}
      data={formData}
      hideSaveToast={() => true}
      onSave={isPartner ? undefined : onSave}
      onDelete={!isCreate && !isPartner ? onDelete : undefined}
    >
      <ConsultationRequestForm
        isCreate={isCreate}
        isPartner={isPartner}
        formData={formData}
        partnerOptions={partnerOptions}
        detailData={data}
      />
    </ContentDetailView>
  )
}

type ConsultationRequestFormProps = {
  isCreate: boolean
  isPartner: boolean
  formData: ConsultationFormData
  partnerOptions: { label: string; value: string | number }[]
  detailData?: IAdminConsultationRequestRO
}

function ConsultationRequestForm({
  isCreate,
  isPartner,
  formData,
  partnerOptions,
  detailData,
}: ConsultationRequestFormProps) {
  const form = useFormContext<ConsultationFormData>()
  const referralPartnerId = form.watch('referralPartnerId')
  const status = form.watch('status')
  const partnerSelected = resolveReferralPartnerId(referralPartnerId) != null
  const customerFieldsReadOnly = !isCreate
  const isCompleted = status === IAdminConsultationRequestRoStatusEnum.COMPLETED

  useEffect(() => {
    if (isCreate && partnerSelected) {
      form.setValue('referrerCodeSnapshot', '')
    }
  }, [form, isCreate, partnerSelected])

  const fields = useMemo(() => {
    const list: FormFieldData<ConsultationFormData>[] = [
      {
        label: '이름',
        key: 'name',
        type: FormFieldType.STRING,
        required: isCreate,
        readOnly: customerFieldsReadOnly,
      },
      {
        label: '연락처',
        key: 'phone',
        type: FormFieldType.STRING,
        required: isCreate,
        readOnly: customerFieldsReadOnly,
      },
      {
        label: '이메일',
        key: 'email',
        type: FormFieldType.STRING,
        required: isCreate,
        readOnly: customerFieldsReadOnly,
        placeholder: isCreate ? '미확인 시 - 입력' : undefined,
      },
      {
        label: '유입 채널',
        key: 'channel',
        type: FormFieldType.SELECT,
        readOnly: customerFieldsReadOnly,
        options: isCreate
          ? CHANNEL_OPTIONS
          : [
              {
                label:
                  CHANNEL_LABELS[formData.channel ?? ''] ??
                  formData.channel ??
                  '',
                value:
                  formData.channel ??
                  IAdminConsultationRequestRoChannelEnum.WEB_FORM,
              },
            ],
      },
      {
        label: '파트너',
        key: 'referralPartnerId',
        type: FormFieldType.SELECT,
        readOnly: isPartner,
        options: partnerOptions,
      },
    ]

    if (isCreate && !partnerSelected) {
      list.push({
        label: '추천인 코드',
        key: 'referrerCodeSnapshot',
        type: FormFieldType.STRING,
        placeholder: '채팅 메시지의 코드 (미등록 코드만 입력)',
      })
    } else if (!isCreate) {
      list.push({
        label: '제출 시 추천인 코드',
        key: 'referrerCodeSnapshot',
        type: FormFieldType.STRING,
        readOnly: true,
      })
    }

    list.push({
      label: '상담 내용',
      key: 'message',
      type: FormFieldType.TEXT,
      required: isCreate,
      readOnly: customerFieldsReadOnly,
    })

    if (!isCreate && detailData?.formPayload) {
      list.push({
        label: '상세 양식 데이터',
        key: 'formPayload',
        type: FormFieldType.CUSTOM_READ_ONLY,
        render: (row) =>
          row?.formPayload ? (
            <ConsultationFormPayloadPanel payload={row.formPayload} />
          ) : (
            '-'
          ),
      })
    }

    if (!isCreate) {
      list.push(
        {
          label: '접수일',
          key: 'createDate',
          type: FormFieldType.STRING,
          readOnly: true,
          resolver: (row) => formatDateTime(row.createDate),
        },
        {
          label: '예약 확정일',
          key: 'reservationConfirmedAt',
          type: FormFieldType.STRING,
          readOnly: true,
          resolver: (row) => formatDateTime(row.reservationConfirmedAt),
        },
      )
    }

    list.push(
      {
        label: '상태',
        key: 'status',
        type: FormFieldType.SELECT,
        readOnly: isPartner,
        options: STATUS_OPTIONS,
      },
      {
        label: '관리자 메모',
        key: 'adminMemo',
        type: FormFieldType.TEXT,
        readOnly: isPartner,
      },
    )

    if (!isCreate && isCompleted && !isPartner) {
      list.push(
        {
          label: '시술명',
          key: 'procedureName',
          type: FormFieldType.STRING,
          required: true,
        },
        {
          label: '매출액',
          key: 'revenueAmount',
          type: FormFieldType.CUSTOM_READ_ONLY,
          required: true,
          render: () => <RevenueAmountField />,
        },
        {
          label: '통화',
          key: 'revenueCurrency',
          type: FormFieldType.SELECT,
          required: true,
          options: CURRENCY_SELECT_OPTIONS,
        },
        {
          label: '매출일',
          key: 'revenueDate',
          type: FormFieldType.DATE,
          required: true,
        },
      )
    }

    return list
  }, [
    customerFieldsReadOnly,
    detailData?.formPayload,
    formData.channel,
    isCompleted,
    isCreate,
    isPartner,
    partnerOptions,
    partnerSelected,
  ])

  return <DetailForm data={formData} fields={fields} />
}
